/**
 * korsha-product.js — purchase behaviour for the KORSHA product page, quick
 * add and product cards.
 *
 *   <korsha-variant-picker>     option selection, resolved by the server
 *   <korsha-product-form>       add to cart through Shopify's cart API
 *   <korsha-sticky-buy>         the sticky add to cart bar (mobile and desktop)
 *   <korsha-recommendations>    Shopify product recommendations, lazily
 *   quick add                   a panel that renders sections/korsha-quick-add
 *
 * Nothing here fakes commerce. Variants are resolved by Shopify through the
 * Section Rendering API (`option_values=`); items are added with a real POST
 * to /cart/add.js. After every add the platform's CartLinesUpdateEvent is
 * dispatched, which is the event Horizon's cart drawer, cart count and cart
 * items listen for — so those keep working without being rebuilt.
 *
 * `@shopify/events` is loaded from Shopify's CDN through Horizon's import map.
 * It is imported lazily: if it ever fails to load, forms fall back to a
 * normal page submit instead of breaking.
 *
 * Every custom element tolerates missing children, so a merchant removing a
 * block in the theme editor cannot make this throw.
 */

const eventsModule = import('@shopify/events').catch(() => null);

// Horizon only registers its analytics view elements (<product-component>,
// <collection-component>) from its own product card script, which KORSHA
// pages do not load. Importing it keeps product view events firing.
import('@theme/view-event-elements').catch(() => {});

const QUICK_ADD_SECTION = 'korsha-quick-add';
const SUCCESS_DURATION = 6000;
const ERROR_DURATION = 3200;
const RETRY_DELAY = 700;

const routes = () => ({
  cartAdd: window.Theme?.routes?.cart_add_url || '/cart/add.js',
  cart: window.Theme?.routes?.cart_url || '/cart',
});

/** @param {string} html */
const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

/** @param {Element} element */
const productRoot = (element) => element.closest('[data-korsha-product]');

const fetchCart = async () => {
  const response = await fetch(`${routes().cart}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
  return response.json();
};

/**
 * Posts an add to cart. Shopify can answer a quick run of adds with 429
 * ("slow down"), which means nothing was added, so that one case is retried
 * once before the shopper is told anything went wrong. A dropped connection
 * is not retried: the first request may have reached Shopify, and a second
 * would put the piece in the bag twice.
 * @param {FormData} formData
 */
async function postAdd(formData) {
  const send = () =>
    fetch(routes().cartAdd, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
      credentials: 'same-origin',
    });

  const response = await send();
  if (response.status !== 429) return response;
  await new Promise((resolve) => window.setTimeout(resolve, RETRY_DELAY));
  return send();
}

/**
 * Which of a card's short labels fits a refused add. Shopify explains a 422
 * in a sentence ("… is already sold out.", "All 2 … are in your cart.", "You
 * can only add 1 …"); a card's control has room for two or three words.
 * @param {number} status
 * @param {string} [description]
 * @returns {'soldOut' | 'limit' | 'error'}
 */
function refusalKind(status, description = '') {
  if (status !== 422) return 'error';
  const text = description.toLowerCase();
  if (text.includes('sold out')) return 'soldOut';
  if (/in your cart|only add|add more|can't add|cannot add/.test(text)) return 'limit';
  return 'error';
}

/**
 * Copies the inner markup of every named region from a freshly rendered
 * section into the page. Regions are matched by name and position, so two
 * price blocks both update.
 *
 * @param {Element} root
 * @param {ParentNode} source
 * @param {string[]} names
 */
function replaceRegions(root, source, names) {
  for (const name of names) {
    const current = root.querySelectorAll(`[data-korsha-region="${name}"]`);
    const fresh = source.querySelectorAll(`[data-korsha-region="${name}"]`);
    current.forEach((element, index) => {
      const next = fresh[index];
      if (next) element.innerHTML = next.innerHTML;
    });
  }
}

/* -------------------------------------------------------------------------
   Variant picker
   ---------------------------------------------------------------------- */

class KorshaVariantPicker extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;
  #bound = false;

  sizeChosen = true;

  connectedCallback() {
    if (this.#bound) return;
    this.#bound = true;

    this.addEventListener('change', this.#onChange);

    const group = this.sizeGroup;
    const hasVariantParam = new URL(window.location.href).searchParams.has('variant');
    const startsChosen = !this.requiresSize || !group || (this.dataset.context === 'main' && hasVariantParam);

    this.sizeChosen = startsChosen;
    if (!startsChosen) this.#clearSizeSelection();

    // Forms render before or after the picker depending on block order.
    queueMicrotask(() => this.#forms().forEach((form) => form.applyState()));
  }

  get requiresSize() {
    return this.hasAttribute('data-require-size');
  }

  /** The size option, when it is a set of buttons with more than one value. */
  get sizeGroup() {
    const group = this.querySelector('[data-korsha-size-option]');
    return group?.querySelector('input[type="radio"]') ? group : null;
  }

  needsSize() {
    return this.requiresSize && !this.sizeChosen && Boolean(this.sizeGroup);
  }

  promptForSize() {
    const group = this.sizeGroup;
    if (!group) return;

    const message = group.querySelector('[data-korsha-size-message]');
    group.classList.add('korsha-option--attention');
    if (message) {
      message.hidden = false;
      message.setAttribute('role', 'alert');
    }

    const target = group.querySelector('input[data-available="true"]') || group.querySelector('input');
    group.scrollIntoView({ block: 'center', behavior: 'smooth' });
    target?.focus({ preventScroll: true });
  }

  #clearSizeSelection() {
    const group = this.sizeGroup;
    if (!group) return;
    group.querySelectorAll('input[type="radio"]').forEach((input) => {
      input.checked = false;
    });
    const value = group.querySelector('[data-korsha-selected-value]');
    if (value) value.textContent = '';
  }

  /** @returns {KorshaProductForm[]} */
  #forms() {
    const root = productRoot(this);
    if (!root) return [];
    return Array.from(root.querySelectorAll('korsha-product-form')).filter(
      (form) => form.dataset.context === this.dataset.context
    );
  }

  /** Option value ids in option order, for `option_values=`. */
  selectedValueIds() {
    return Array.from(this.querySelectorAll('[data-korsha-option-group]'))
      .map((group) => {
        const select = group.querySelector('select[data-korsha-option]');
        if (select) return select.selectedOptions[0]?.dataset.optionValueId;

        const single = group.querySelector('input[type="hidden"][data-korsha-option]');
        if (single) return single.dataset.optionValueId;

        const checked =
          group.querySelector('input[data-korsha-option]:checked') ||
          group.querySelector('input[data-korsha-option][data-server-checked]');
        return checked?.dataset.optionValueId;
      })
      .filter(Boolean);
  }

  selectedOptions() {
    return Array.from(this.querySelectorAll('[data-korsha-option-group]'))
      .map((group) => {
        const select = group.querySelector('select[data-korsha-option]');
        const input = select || group.querySelector('input[data-korsha-option]:checked, input[type="hidden"][data-korsha-option]');
        if (!input) return null;
        return { name: input.dataset.optionName || '', value: input.value };
      })
      .filter(Boolean);
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement || input instanceof HTMLSelectElement)) return;
    if (!input.hasAttribute('data-korsha-option')) return;

    const group = input.closest('[data-korsha-option-group]');
    if (group?.hasAttribute('data-korsha-size-option')) {
      this.sizeChosen = true;
      group.classList.remove('korsha-option--attention');
      const message = group.querySelector('[data-korsha-size-message]');
      if (message) message.hidden = true;
    }

    // Show the choice immediately; the server confirms price and availability.
    const selectedValue = group?.querySelector('[data-korsha-selected-value]');
    if (selectedValue && input instanceof HTMLInputElement) selectedValue.textContent = input.value;

    const option = input instanceof HTMLSelectElement ? input.selectedOptions[0] : input;
    const connectedUrl = option?.dataset.productUrl;
    const currentUrl = this.dataset.productUrl;

    // Combined listings: this value belongs to a different product.
    if (connectedUrl && currentUrl && connectedUrl.split('?')[0] !== currentUrl.split('?')[0]) {
      if (this.dataset.context === 'quick-add') {
        QuickAdd.load(connectedUrl);
      } else {
        window.location.assign(connectedUrl);
      }
      return;
    }

    this.#forms().forEach((form) => form.applyState());
    this.#update();
  };

  async #update() {
    const root = productRoot(this);
    const sectionId = this.dataset.sectionId;
    const productUrl = (this.dataset.productUrl || '').split('?')[0];
    if (!root || !sectionId || !productUrl) return;

    this.#abort?.abort();
    this.#abort = new AbortController();

    const ids = this.selectedValueIds();
    const url = `${productUrl}?section_id=${encodeURIComponent(sectionId)}&option_values=${ids.join(',')}`;

    this.setAttribute('aria-busy', 'true');
    this.#forms().forEach((form) => form.setBusy(true));

    const events = await eventsModule;
    const deferred = events?.ProductSelectEvent?.createPromise?.();
    const selectedOptions = this.selectedOptions();
    if (events && deferred && selectedOptions.length) {
      this.dispatchEvent(
        new events.ProductSelectEvent({
          product: {
            id: this.dataset.productId || '',
            title: this.dataset.productTitle || '',
            handle: this.dataset.productHandle || '',
          },
          selectedOptions,
          promise: deferred.promise,
        })
      );
    }

    try {
      const response = await fetch(url, { signal: this.#abort.signal });
      if (!response.ok) throw new Error(`Variant request failed: ${response.status}`);
      const doc = parseHTML(await response.text());

      const freshRoot = doc.querySelector(`[data-korsha-product="${sectionId}"]`) || doc;
      const freshPicker = freshRoot.querySelector('korsha-variant-picker');
      if (!freshPicker) throw new Error('Variant picker missing from response');

      const json = freshPicker.querySelector('[data-korsha-variant-json]')?.textContent || 'null';
      const variant = JSON.parse(json);

      this.#syncFrom(freshPicker);
      replaceRegions(root, freshRoot, ['price', 'sku', 'inventory', 'rules', 'sticky-price', 'sticky-media', 'sticky-variant', 'quick-media']);
      this.#syncPlans(root, freshRoot);

      const freshForms = Array.from(freshRoot.querySelectorAll('korsha-product-form')).filter(
        (form) => form.getAttribute('data-context') === this.dataset.context
      );
      this.#forms().forEach((form, index) => form.syncFrom(freshForms[index], variant));

      if (this.dataset.context === 'main') {
        const pageUrl = new URL(window.location.href);
        if (variant?.id) {
          pageUrl.searchParams.set('variant', variant.id);
        } else {
          pageUrl.searchParams.delete('variant');
        }
        window.history.replaceState(window.history.state, '', pageUrl.toString());
      }

      root.dispatchEvent(new CustomEvent('korsha:variant-change', { bubbles: true, detail: { variant } }));

      if (deferred) {
        const payload = doc.querySelector('[view-event-payload]')?.getAttribute('view-event-payload');
        let selectedVariant = null;
        try {
          selectedVariant = payload ? JSON.parse(payload)?.product?.selectedVariant ?? null : null;
        } catch {
          selectedVariant = null;
        }
        deferred.resolve({ variant: variant ? selectedVariant : null, detail: { resource: variant } });
      }
    } catch (error) {
      if (error?.name === 'AbortError') return;
      deferred?.reject(error);
      console.error('[korsha] variant update failed', error);
    } finally {
      this.removeAttribute('aria-busy');
      this.#forms().forEach((form) => form.setBusy(false));
    }
  }

  /**
   * Brings availability and selection across from the server's picker without
   * replacing the elements, so keyboard focus stays where the shopper left it.
   *
   * @param {Element} freshPicker
   */
  #syncFrom(freshPicker) {
    const sizeGroup = this.sizeGroup;

    freshPicker.querySelectorAll('input[type="radio"][data-korsha-option]').forEach((fresh) => {
      const current = this.querySelector(
        `input[type="radio"][data-option-value-id="${fresh.getAttribute('data-option-value-id')}"]`
      );
      if (!(current instanceof HTMLInputElement)) return;

      current.dataset.available = fresh.getAttribute('data-available') || 'true';
      current.dataset.productUrl = fresh.getAttribute('data-product-url') || '';
      current.toggleAttribute('data-server-checked', fresh.hasAttribute('checked'));

      const inSizeGroup = sizeGroup?.contains(current);
      current.checked = inSizeGroup && !this.sizeChosen ? false : fresh.hasAttribute('checked');

      const freshText = fresh.nextElementSibling?.querySelector('[data-korsha-unavailable-text]');
      const currentText = current.nextElementSibling?.querySelector('[data-korsha-unavailable-text]');
      if (freshText && currentText) currentText.textContent = freshText.textContent;
    });

    freshPicker.querySelectorAll('select[data-korsha-option]').forEach((freshSelect, index) => {
      const currentSelect = this.querySelectorAll('select[data-korsha-option]')[index];
      if (!(currentSelect instanceof HTMLSelectElement)) return;
      Array.from(freshSelect.querySelectorAll('option')).forEach((freshOption, optionIndex) => {
        const currentOption = currentSelect.options[optionIndex];
        if (!currentOption) return;
        currentOption.textContent = freshOption.textContent;
        currentOption.dataset.available = freshOption.getAttribute('data-available') || 'true';
        if (freshOption.hasAttribute('selected')) currentSelect.selectedIndex = optionIndex;
      });
      const label = currentSelect.parentElement?.querySelector('.korsha-select__value');
      if (label) label.textContent = currentSelect.selectedOptions[0]?.textContent?.split(' — ')[0]?.trim() || '';
    });

    const freshValues = freshPicker.querySelectorAll('[data-korsha-selected-value]');
    this.querySelectorAll('[data-korsha-selected-value]').forEach((element, index) => {
      const inSizeGroup = sizeGroup?.contains(element);
      element.textContent = inSizeGroup && !this.sizeChosen ? '' : freshValues[index]?.textContent?.trim() || '';
    });
  }

  /**
   * Selling plan prices change with the variant; the chosen plan must not.
   * @param {Element} root
   * @param {ParentNode} freshRoot
   */
  #syncPlans(root, freshRoot) {
    const plans = root.querySelector('[data-korsha-region="plans"]');
    const freshPlans = freshRoot.querySelector('[data-korsha-region="plans"]');
    if (!plans || !freshPlans) return;
    const chosen = plans.querySelector('input:checked')?.value;
    plans.innerHTML = freshPlans.innerHTML;
    if (chosen === undefined) return;
    const match = Array.from(plans.querySelectorAll('input')).find((input) => input.value === chosen);
    if (match) match.checked = true;
  }
}

/* -------------------------------------------------------------------------
   Product form
   ---------------------------------------------------------------------- */

export class KorshaProductForm extends HTMLElement {
  #bound = false;
  /** @type {number | undefined} */
  #resetTimer;
  /** @type {number | undefined} */
  #successTimer;

  connectedCallback() {
    if (this.#bound) return;
    this.#bound = true;

    const button = this.button;
    if (button && this.label) {
      button.dataset.serverLabel = this.label.textContent?.trim() || '';
      button.dataset.serverDisabled = String(button.disabled);
    }

    this.form?.addEventListener('submit', this.#onSubmit);
    this.addEventListener('click', this.#onClick);
    this.querySelector('[data-korsha-quantity-input]')?.addEventListener('change', () => this.#syncSteppers());
    this.#syncSteppers();
    this.applyState();
  }

  get form() {
    return this.querySelector('form');
  }

  get button() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-korsha-add]'));
  }

  get label() {
    return this.querySelector('[data-korsha-add-label]');
  }

  get picker() {
    const root = productRoot(this);
    const picker = root?.querySelector('korsha-variant-picker');
    return picker instanceof KorshaVariantPicker && picker.dataset.context === this.dataset.context ? picker : null;
  }

  /** Works out the button's label and state from the server state and the picker. */
  applyState() {
    const button = this.button;
    if (!button || this.dataset.context === 'card') return;
    if (button.dataset.state === 'loading' || button.dataset.state === 'added') return;

    const needsSize = Boolean(this.picker?.needsSize());
    const serverDisabled = button.dataset.serverDisabled === 'true';
    const label = needsSize ? this.picker?.dataset.selectLabel || '' : button.dataset.serverLabel || '';

    button.disabled = needsSize ? false : serverDisabled;
    if (this.label) this.label.textContent = label;

    const dynamic = this.querySelector('[data-korsha-dynamic]');
    if (dynamic instanceof HTMLElement) dynamic.hidden = needsSize || serverDisabled;

    this.#broadcast(label, button.disabled);
  }

  /** @param {boolean} busy */
  setBusy(busy) {
    this.toggleAttribute('data-busy', busy);
  }

  /**
   * @param {Element | undefined} fresh - The same form, freshly rendered for the new selection.
   * @param {{ id: number } | null} variant
   */
  syncFrom(fresh, variant) {
    const button = this.button;
    const freshButton = fresh?.querySelector('[data-korsha-add]');
    if (button && freshButton) {
      button.dataset.serverLabel = freshButton.querySelector('[data-korsha-add-label]')?.textContent?.trim() || '';
      button.dataset.serverDisabled = String(freshButton.hasAttribute('disabled'));
    }

    const idInput = this.querySelector('[data-korsha-variant-input]');
    if (idInput instanceof HTMLInputElement) {
      idInput.value = variant?.id ? String(variant.id) : '';
      idInput.disabled = !variant;
    }

    const quantity = this.querySelector('[data-korsha-quantity-input]');
    const freshQuantity = fresh?.querySelector('[data-korsha-quantity-input]');
    if (quantity instanceof HTMLInputElement && freshQuantity) {
      for (const attribute of ['min', 'max', 'step']) {
        const value = freshQuantity.getAttribute(attribute);
        if (value === null) quantity.removeAttribute(attribute);
        else quantity.setAttribute(attribute, value);
      }
      const min = Number(quantity.min) || 1;
      if (Number(quantity.value) < min) quantity.value = String(min);
      if (quantity.max && Number(quantity.value) > Number(quantity.max)) quantity.value = quantity.max;
      this.#syncSteppers();
    }

    this.#hideMessages();
    this.applyState();
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const step = target.closest('[data-korsha-quantity-step]');
    if (step) {
      event.preventDefault();
      const input = this.querySelector('[data-korsha-quantity-input]');
      if (!(input instanceof HTMLInputElement)) return;
      const direction = Number(step.getAttribute('data-korsha-quantity-step'));
      try {
        if (direction > 0) input.stepUp();
        else input.stepDown();
      } catch {
        return;
      }
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const cartLink = target.closest('[data-korsha-open-cart]');
    if (cartLink) {
      const drawer = document.querySelector('theme-drawer#cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        event.preventDefault();
        drawer.open();
      }
    }
  };

  #syncSteppers() {
    const input = this.querySelector('[data-korsha-quantity-input]');
    if (!(input instanceof HTMLInputElement) || input.type !== 'number') return;
    const value = Number(input.value);
    const down = this.querySelector('[data-korsha-quantity-step="-1"]');
    const up = this.querySelector('[data-korsha-quantity-step="1"]');
    if (down instanceof HTMLButtonElement) down.disabled = value <= (Number(input.min) || 1);
    if (up instanceof HTMLButtonElement) up.disabled = Boolean(input.max) && value >= Number(input.max);
  }

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    event.preventDefault();

    const form = this.form;
    const button = this.button;
    if (!form || !button) return;

    const picker = this.picker;
    if (picker?.needsSize()) {
      picker.promptForSize();
      this.#announce(picker.dataset.selectLabel || '');
      return;
    }

    if (button.disabled || button.dataset.state === 'loading') return;

    const formData = new FormData(form);
    const variantId = formData.get('id');
    if (!variantId) return;

    const events = await eventsModule;
    if (!events?.CartLinesUpdateEvent) {
      // The standard events module is unavailable: a plain submit still adds
      // the item and takes the shopper to the cart.
      form.submit();
      return;
    }

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => element instanceof HTMLElement && element.dataset.sectionId)
      .filter(Boolean);
    if (sectionIds.length) {
      formData.append('sections', sectionIds.join(','));
      formData.append('sections_url', window.location.pathname);
    }

    const quantity = Number(formData.get('quantity')) || 1;
    const deferred = events.CartLinesUpdateEvent.createPromise();

    this.#hideMessages();
    this.#setState('loading');

    this.dispatchEvent(
      new events.CartLinesUpdateEvent({
        action: 'add',
        context: this.dataset.context === 'quick-add' ? 'dialog' : 'product',
        lines: [{ merchandiseId: String(variantId), quantity }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await postAdd(formData);
      // A refusal that is not JSON (an error page) still reads as a refusal,
      // not a crash; an accepted add always reads as added.
      const data = await response.json().catch(() => (response.ok ? {} : { status: response.status }));

      if (!response.ok || data.status) {
        const message = data.description || data.message || button.dataset.errorLabel || '';
        this.dispatchEvent(
          new events.CartErrorEvent({
            error: data.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: data.description },
          })
        );

        const cart = await fetchCart().catch(() => null);
        deferred.resolve({
          cart: cart ? events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : null,
          detail: {
            didError: true,
            items: cart?.items,
            source: 'korsha-product-form',
            itemCount: quantity,
            productId: this.dataset.productId,
          },
        });

        this.#fail(message, refusalKind(response.status, data.description));
        return;
      }

      // Shopify has confirmed the item is in the cart: say so now. Re-reading
      // the whole cart for the drawer and analytics happens after, so the
      // shopper never waits on a second request to see the result — and a
      // failed re-read can never report a successful add as an error.
      this.#setState('added');
      this.#showSuccess();
      this.dispatchEvent(new CustomEvent('korsha:added', { bubbles: true, detail: { item: data } }));

      fetchCart()
        .then((cart) =>
          deferred.resolve({
            cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: {
              items: cart.items,
              source: 'korsha-product-form',
              itemCount: quantity,
              productId: this.dataset.productId,
              sections: data.sections,
              didError: false,
            },
          })
        )
        .catch((error) => deferred.reject(error));
    } catch (error) {
      console.error('[korsha] add to cart failed', error);
      deferred.reject(error);
      this.dispatchEvent(
        new events.CartErrorEvent({
          error: error instanceof Error ? error.message : 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      this.#fail(button.dataset.errorLabel || '');
    }
  };

  /**
   * A failed add. The product page and quick add show Shopify's message under
   * the button; a card has no room for that, so its control gives the short
   * reason ("Sold out", "All in your bag", "Not added") for a moment and the
   * full message is announced.
   * @param {string} message
   * @param {'soldOut' | 'limit' | 'error'} [kind]
   */
  #fail(message, kind = 'error') {
    if (this.dataset.context === 'card') {
      this.#setState('error', kind);
      this.#announce(message);
      return;
    }
    this.#setState(null);
    this.#showError(message);
  }

  /**
   * @param {'loading' | 'added' | 'error' | null} state
   * @param {'soldOut' | 'limit' | 'error'} [kind] - For an error, which of the card's labels to show.
   */
  #setState(state, kind = 'error') {
    const button = this.button;
    if (!button) return;
    window.clearTimeout(this.#resetTimer);

    if (state === null) {
      delete button.dataset.state;
      button.removeAttribute('aria-busy');
      if (this.dataset.context === 'card' && this.label) {
        this.label.textContent = button.dataset.serverLabel || this.label.textContent;
      }
      this.applyState();
      return;
    }

    button.dataset.state = state;

    if (state === 'loading') {
      button.setAttribute('aria-busy', 'true');
      // The words change as well as the hairline, so the shopper is never
      // unsure whether the tap registered.
      const adding = button.dataset.addingLabel || '';
      if (adding && this.label) this.label.textContent = adding;
      this.#broadcast(this.label?.textContent || '', true);
      return;
    }

    if (state === 'error') {
      button.removeAttribute('aria-busy');
      const text = button.dataset[`${kind}Label`] || button.dataset.errorLabel || '';
      if (text && this.label) this.label.textContent = text;
      this.#resetTimer = window.setTimeout(() => this.#setState(null), ERROR_DURATION);
      return;
    }

    button.removeAttribute('aria-busy');
    const added = button.dataset.addedLabel || window.Theme?.translations?.added || '';
    if (added && this.label) this.label.textContent = added;
    this.#announce(added);
    this.#broadcast(added, false);

    this.#resetTimer = window.setTimeout(() => this.#setState(null), 2200);
  }

  #showSuccess() {
    const success = this.querySelector('[data-korsha-success]');
    if (!(success instanceof HTMLElement)) return;
    success.hidden = false;
    window.clearTimeout(this.#successTimer);
    this.#successTimer = window.setTimeout(() => {
      success.hidden = true;
    }, SUCCESS_DURATION);
  }

  /** @param {string} message */
  #showError(message) {
    const error = this.querySelector('[data-korsha-error]');
    if (error instanceof HTMLElement && message) {
      error.textContent = message;
      error.hidden = false;
    } else if (message) {
      this.#announce(message);
    }
  }

  #hideMessages() {
    const error = this.querySelector('[data-korsha-error]');
    if (error instanceof HTMLElement) error.hidden = true;
    const success = this.querySelector('[data-korsha-success]');
    if (success instanceof HTMLElement) success.hidden = true;
  }

  /** @param {string} text */
  #announce(text) {
    const live = this.querySelector('[data-korsha-live]');
    if (!live) return;
    live.textContent = '';
    window.setTimeout(() => {
      live.textContent = text;
    }, 50);
  }

  /**
   * Tells the sticky bar what the main button currently says.
   * @param {string} label
   * @param {boolean} disabled
   */
  #broadcast(label, disabled) {
    if (this.dataset.context !== 'main') return;
    this.dispatchEvent(new CustomEvent('korsha:buy-state', { bubbles: true, detail: { label, disabled } }));
  }
}

/* -------------------------------------------------------------------------
   Sticky add to cart
   ---------------------------------------------------------------------- */

class KorshaStickyBuy extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    const root = productRoot(this);
    const form = document.getElementById(this.dataset.formId || '');
    const mainButton = form?.querySelector('[data-korsha-add]');
    const button = this.querySelector('[data-korsha-sticky-add]');
    if (!root || !(form instanceof HTMLFormElement) || !mainButton || !(button instanceof HTMLButtonElement)) return;

    // Shown only once the main button has scrolled up out of view — never
    // while it is still below the fold, and never at the same time as it.
    this.#observer = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      const visible = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.classList.toggle('is-visible', visible);
      this.toggleAttribute('inert', !visible);
    });
    this.#observer.observe(mainButton);

    root.addEventListener('korsha:buy-state', (event) => {
      const { label, disabled } = /** @type {CustomEvent} */ (event).detail || {};
      if (typeof label === 'string') button.textContent = label;
      button.disabled = Boolean(disabled);

      // Until a size is chosen the bar must not claim one: the variant line
      // waits, and the button reads "Select a size" like the main one.
      const picker = root.querySelector('korsha-variant-picker');
      const variantLine = this.querySelector('[data-korsha-sticky-variant]');
      if (variantLine instanceof HTMLElement) {
        variantLine.hidden = picker instanceof KorshaVariantPicker && picker.dataset.context === 'main' && picker.needsSize();
      }
    });

    button.addEventListener('click', () => {
      const picker = root.querySelector('korsha-variant-picker');
      if (picker instanceof KorshaVariantPicker && picker.dataset.context === 'main' && picker.needsSize()) {
        picker.promptForSize();
        return;
      }
      form.requestSubmit(/** @type {HTMLButtonElement} */ (mainButton));
    });
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }
}

/* -------------------------------------------------------------------------
   Quick add
   ---------------------------------------------------------------------- */

const CLOSE_ICON =
  '<svg class="korsha-icon" viewBox="0 0 20 20" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1" aria-hidden="true" focusable="false"><path d="M4.5 4.5l11 11M15.5 4.5l-11 11"/></svg>';

const QuickAdd = {
  /** @type {HTMLDialogElement | null} */
  dialog: null,
  /** @type {AbortController | null} */
  abort: null,

  /** @param {HTMLElement} [trigger] */
  ensure(trigger) {
    if (this.dialog?.isConnected) return this.dialog;

    const dialog = document.createElement('dialog');
    dialog.className = 'korsha-drawer korsha-drawer--side korsha-quick-panel';
    dialog.setAttribute('aria-labelledby', 'KorshaQuickAddTitle');
    dialog.innerHTML = `
      <div class="korsha-panel">
        <div class="korsha-panel__head">
          <p class="korsha-panel__title" data-korsha-quick-heading></p>
          <button type="button" class="korsha-panel__close" data-korsha-drawer-close>${CLOSE_ICON}</button>
        </div>
        <div class="korsha-panel__body" data-korsha-quick-body></div>
      </div>`;

    const heading = dialog.querySelector('[data-korsha-quick-heading]');
    if (heading) heading.textContent = trigger?.dataset.panelTitle || '';
    const close = dialog.querySelector('[data-korsha-drawer-close]');
    close?.setAttribute('aria-label', trigger?.dataset.closeLabel || 'Close');

    document.body.appendChild(dialog);
    this.dialog = dialog;
    return dialog;
  },

  /**
   * @param {string} url
   * @param {HTMLElement} [trigger]
   */
  open(url, trigger) {
    const dialog = this.ensure(trigger);
    if (!dialog.open) {
      dialog.showModal();
      // korsha.js releases this when the drawer closes.
      if (window.KorshaDrawers) window.KorshaDrawers.lockScroll();
      else document.documentElement.setAttribute('scroll-lock', '');
    }
    this.load(url);
  },

  /** @param {string} url */
  async load(url) {
    const dialog = this.dialog;
    const body = dialog?.querySelector('[data-korsha-quick-body]');
    if (!dialog || !body) return;

    this.abort?.abort();
    this.abort = new AbortController();

    body.setAttribute('aria-busy', 'true');
    body.innerHTML = '<div class="korsha-quick-panel__loading"></div>';

    const productUrl = url.split('?')[0];
    try {
      const response = await fetch(`${productUrl}?section_id=${QUICK_ADD_SECTION}`, { signal: this.abort.signal });
      if (!response.ok) throw new Error(`Quick add request failed: ${response.status}`);
      const content = parseHTML(await response.text()).querySelector('.korsha-quick');
      if (!content) throw new Error('Quick add content missing');

      body.replaceChildren(document.importNode(content, true));
      body.removeAttribute('aria-busy');
      body.querySelector('input:not([type="hidden"]), select, button')?.focus({ preventScroll: true });
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('[korsha] quick add failed', error);
      // The product page is always a correct fallback.
      window.location.assign(productUrl);
    }
  },
};

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element) || event.defaultPrevented) return;
  const trigger = event.target.closest('[data-korsha-quick-add]');
  if (!(trigger instanceof HTMLElement)) return;
  // Let modified clicks open the product page in a new tab as normal.
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

  event.preventDefault();
  QuickAdd.open(trigger.dataset.korshaQuickAdd || trigger.getAttribute('href') || '', trigger);
});

document.addEventListener('korsha:added', (event) => {
  const dialog = event.target instanceof Element ? event.target.closest('.korsha-quick-panel') : null;
  if (dialog instanceof HTMLDialogElement && dialog.open) {
    // A beat to let the "Added" state register before the panel closes.
    window.setTimeout(() => dialog.close(), 700);
  }
});

/* -------------------------------------------------------------------------
   Recommendations
   ---------------------------------------------------------------------- */

class KorshaRecommendations extends HTMLElement {
  #started = false;

  connectedCallback() {
    if (this.#started || this.hasAttribute('data-loaded') || !this.dataset.url) return;
    this.#started = true;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        this.#load();
      },
      { rootMargin: '0px 0px 600px 0px' }
    );
    observer.observe(this);
  }

  async #load() {
    try {
      const response = await fetch(this.dataset.url || '');
      if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`);
      const fresh = parseHTML(await response.text()).querySelector('korsha-recommendations [data-korsha-recommendations-content]');
      if (!fresh) {
        this.#hide();
        return;
      }
      this.replaceChildren(document.importNode(fresh, true));
      this.setAttribute('data-loaded', '');
    } catch (error) {
      console.error('[korsha] recommendations failed', error);
      this.#hide();
    }
  }

  #hide() {
    // In the editor an empty section should stay selectable.
    if (window.Shopify?.designMode) return;
    this.closest('.shopify-section')?.setAttribute('hidden', '');
  }
}

/* -------------------------------------------------------------------------
   Registration
   ---------------------------------------------------------------------- */

const definitions = {
  'korsha-variant-picker': KorshaVariantPicker,
  'korsha-product-form': KorshaProductForm,
  'korsha-sticky-buy': KorshaStickyBuy,
  'korsha-recommendations': KorshaRecommendations,
};

for (const [name, constructor] of Object.entries(definitions)) {
  if (!customElements.get(name)) customElements.define(name, constructor);
}
